import React, { useEffect, useState } from 'react';

import {
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Flex,
  Icon,
  Select,
} from '@chakra-ui/react';
import {
  MdReceiptLong,
  MdLocalShipping,
  MdStorefront,
  MdInventory2,
} from 'react-icons/md';
import { FaAmazon, FaEbay } from 'react-icons/fa';
import { useDispatch } from 'react-redux';

import { getOrdersOverviewApi } from 'features/orders/orders.api';
import { useColors } from './colors';

const REVENUE_PERIODS = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
  { value: '0', label: 'All time' },
];

const formatMoney = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const periodLabel = (days) => {
  const match = REVENUE_PERIODS.find((p) => p.value === String(days));
  return match?.label ?? '30 days';
};

const PeriodSelect = ({ value, onChange, loading, ariaLabel }) => (
  <Select
    size="xs"
    w="auto"
    minW="88px"
    value={value}
    onChange={onChange}
    isDisabled={loading}
    aria-label={ariaLabel}
  >
    {REVENUE_PERIODS.map((p) => (
      <option key={p.value} value={p.value}>
        {p.label}
      </option>
    ))}
  </Select>
);

const OrdersSummaryStats = ({ overview, loading }) => {
  const dispatch = useDispatch();
  const { cardBg, borderColor, textColor, mutedTextColor } = useColors();
  const [revenueDays, setRevenueDays] = useState(
    String(overview?.revenuePeriodDays ?? 30),
  );

  useEffect(() => {
    if (overview?.revenuePeriodDays != null) {
      setRevenueDays(String(overview.revenuePeriodDays));
    }
  }, [overview?.revenuePeriodDays]);

  const handleRevenuePeriodChange = (e) => {
    const next = e.target.value;
    setRevenueDays(next);
    dispatch(getOrdersOverviewApi({ revenueDays: Number(next) }));
  };

  const revenuePeriodText = periodLabel(revenueDays);
  const sharedScopeHelp = 'All marketplaces';

  const stats = [
    {
      key: 'totalOrders',
      label: 'Total orders',
      value: overview.totalOrders?.toLocaleString() ?? '0',
      help: sharedScopeHelp,
      icon: MdReceiptLong,
      color: 'blue.400',
    },
    {
      key: 'awaitingShipment',
      label: 'Awaiting shipment',
      value: overview.awaitingShipment?.toLocaleString() ?? '0',
      help: 'Paid, not yet shipped · all marketplaces',
      icon: MdLocalShipping,
      color: 'orange.400',
    },
    {
      key: 'shippedToday',
      label: 'Shipped today',
      value: overview.shippedToday?.toLocaleString() ?? '0',
      help: 'Fulfilled today · all marketplaces',
      icon: MdLocalShipping,
      color: 'green.400',
    },
    {
      key: 'ebayOrders',
      label: 'eBay orders',
      value: overview.ebayOrders?.toLocaleString() ?? '0',
      help: 'All eBay orders',
      icon: FaEbay,
      color: 'red.400',
    },
    {
      key: 'amazonOrders',
      label: 'Amazon orders',
      value: overview.amazonOrders?.toLocaleString() ?? '0',
      help: 'All Amazon orders',
      color: 'orange.300',
    },
    {
      key: 'avgOrderValue',
      label: 'Avg order value',
      value: formatMoney(overview.avgOrderValue ?? 0),
      help: `${revenuePeriodText} · ${sharedScopeHelp}`,
      icon: MdReceiptLong,
      color: 'purple.400',
    },
  ];

  const revenueValue =
    overview.totalRevenue ?? overview.totalRevenue30d ?? 0;
  const quantitySoldValue = overview.totalQuantitySold ?? 0;

  return (
    <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
      {stats.map((stat) => (
        <Box
          key={stat.key}
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
          opacity={loading ? 0.7 : 1}
        >
          <Flex justify="space-between" align="center" gap={2}>
            <Stat size="sm" flex="1" minW={0}>
              <StatLabel color={textColor} fontSize="xs" noOfLines={1}>
                {stat.label}
              </StatLabel>
              <StatNumber color={textColor} fontSize="xl" lineHeight="1.2">
                {stat.value}
              </StatNumber>
              <StatHelpText color={mutedTextColor} fontSize="xs" mb={0} noOfLines={2}>
                {stat.help}
              </StatHelpText>
            </Stat>
            <Icon as={stat.icon} boxSize={7} color={stat.color} flexShrink={0} />
          </Flex>
        </Box>
      ))}

      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={4}
        opacity={loading ? 0.7 : 1}
      >
        <Flex justify="space-between" align="flex-start" gap={2}>
          <Stat size="sm" flex="1" minW={0}>
            <Flex align="center" gap={2} mb={1} flexWrap="wrap">
              <StatLabel color={textColor} fontSize="xs" mb={0}>
                Revenue
              </StatLabel>
              <PeriodSelect
                value={revenueDays}
                onChange={handleRevenuePeriodChange}
                loading={loading}
                ariaLabel="Revenue time period"
              />
            </Flex>
            <StatNumber color={textColor} fontSize="xl" lineHeight="1.2">
              {formatMoney(revenueValue)}
            </StatNumber>
            <StatHelpText color={mutedTextColor} fontSize="xs" mb={0} noOfLines={2}>
              Gross order totals · {revenuePeriodText} · {sharedScopeHelp}
            </StatHelpText>
          </Stat>
          <Icon as={MdStorefront} boxSize={7} color="teal.400" flexShrink={0} mt={1} />
        </Flex>
      </Box>

      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={4}
        opacity={loading ? 0.7 : 1}
      >
        <Flex justify="space-between" align="flex-start" gap={2}>
          <Stat size="sm" flex="1" minW={0}>
            <Flex align="center" gap={2} mb={1} flexWrap="wrap">
              <StatLabel color={textColor} fontSize="xs" mb={0}>
                Total quantity sold
              </StatLabel>
              <PeriodSelect
                value={revenueDays}
                onChange={handleRevenuePeriodChange}
                loading={loading}
                ariaLabel="Quantity sold time period"
              />
            </Flex>
            <StatNumber color={textColor} fontSize="xl" lineHeight="1.2">
              {quantitySoldValue.toLocaleString()}
            </StatNumber>
            <StatHelpText color={mutedTextColor} fontSize="xs" mb={0} noOfLines={2}>
              Units across line items · {revenuePeriodText} · {sharedScopeHelp}
            </StatHelpText>
          </Stat>
          <Icon as={MdInventory2} boxSize={7} color="cyan.400" flexShrink={0} mt={1} />
        </Flex>
      </Box>
    </SimpleGrid>
  );
};

export default OrdersSummaryStats;
